import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import { asyncStatus } from "../../../utils/asyncStatus";
import {
  Avatar,
  Box,
  Chip,
  CircularProgress,
  Container,
  Grid,
  Stack,
  Typography,
} from "@mui/material";
import { getUserByIdAsync, updateVendorStatusAsync } from "../../../services/DashboardUsers";
import { themeOrange, themeBlue } from "../../../utils/colorTheme";
import ReactStars from "react-rating-stars-component";
import VendorServicesTable from "../../../component/common/customTable/VendorServicesTable";
import CustomSelectBox from "../../../component/common/selectComp/CustomSelectBox";
import ButtonComp from "../../../component/common/ButtonComp";
import { IoMail, IoCall } from "react-icons/io5";
import { MdCheckCircle, MdCancel, MdPending } from "react-icons/md";

const statusConfig = {
  1: { label: "Approved", color: "#2E7D32", bg: "#E8F5E9", icon: <MdCheckCircle size={14} /> },
  2: { label: "Rejected", color: "#C62828", bg: "#FFEBEE", icon: <MdCancel size={14} /> },
  0: { label: "Pending", color: "#F9A825", bg: "#FFF8E1", icon: <MdPending size={14} /> },
};

const VendorProfileTab = () => {
  const { id } = useParams();
  const dispatch = useDispatch();

  const {
    get_user_by_id_status,
    get_user_by_id_data,
    update_vendor_status_status,
  } = useSelector((state) => state.dashboard_users);

  const isLoading = update_vendor_status_status === asyncStatus.LOADING;
  const [status, setStatus] = useState({});

  useEffect(() => {
    dispatch(getUserByIdAsync(id));
  }, []);

  const statusArr = [
    { label: "Approved", value: 1 },
    { label: "Rejected", value: 2 },
    { label: "Pending", value: 0 },
  ];

  useEffect(() => {
    if (Object.keys(get_user_by_id_data || {})?.length > 0) {
      setStatus(
        statusArr.find((option) => option.value === +get_user_by_id_data?.status) || null
      );
    }
  }, [get_user_by_id_data]);

  const _handleStatusUpdate = () => {
    const finalObj = { user_id: id, status: status.value };
    dispatch(updateVendorStatusAsync(finalObj));
  };

  const currentStatus = statusConfig[+get_user_by_id_data?.status] || statusConfig[0];

  if (get_user_by_id_status === asyncStatus.LOADING) {
    return (
      <Stack alignItems="center" justifyContent="center" height="60vh">
        <CircularProgress size={30} sx={{ color: themeOrange }} />
      </Stack>
    );
  }

  return (
    <Stack sx={{ pb: 4 }}>
      <Container maxWidth="lg">
        <Stack gap={3} mt={2}>
          {/* Pro Profile Card */}
          <Stack
            sx={{
              bgcolor: "#fff",
              borderRadius: "14px",
              boxShadow: "0 1px 8px rgba(0,0,0,0.06)",
              overflow: "hidden",
            }}
          >
            {/* Header banner */}
            <Box
              sx={{
                height: "120px",
                background: "linear-gradient(135deg, #071d5b 0%, #0d2d7a 100%)",
              }}
            />
            {/* Profile info */}
            <Stack sx={{ px: 3, pb: 3, mt: "-36px" }}>
              <Avatar
                src={get_user_by_id_data?.avatar || ""}
                sx={{
                  width: 76,
                  height: 76,
                  border: "4px solid #fff",
                  boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                  bgcolor: themeOrange,
                  fontSize: "22px",
                  fontWeight: 700,
                }}
              >
                {get_user_by_id_data?.first_name?.[0]}{get_user_by_id_data?.last_name?.[0]}
              </Avatar>
              <Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" alignItems={{ xs: "center", sm: "center" }} gap={2} sx={{ width: "100%", mt: 1.5 }}>
                <Stack alignItems={{ xs: "center", sm: "flex-start" }}>
                  <Typography sx={{ fontSize: "20px", fontWeight: 700, color: themeBlue, textTransform: "capitalize" }}>
                    {get_user_by_id_data?.first_name || ""} {get_user_by_id_data?.last_name || ""}
                  </Typography>
                  <Stack direction="row" gap={2} mt={0.5} flexWrap="wrap">
                    <Stack direction="row" alignItems="center" gap={0.5}>
                      <IoMail size={13} color="#999" />
                      <Typography sx={{ fontSize: "13px", color: "#666" }}>
                        {get_user_by_id_data?.email || ""}
                      </Typography>
                    </Stack>
                    {get_user_by_id_data?.phone && (
                      <Stack direction="row" alignItems="center" gap={0.5}>
                        <IoCall size={13} color="#999" />
                        <Typography sx={{ fontSize: "13px", color: "#666" }}>
                          {get_user_by_id_data?.phone}
                        </Typography>
                      </Stack>
                    )}
                  </Stack>
                </Stack>
                <Stack direction="row" gap={2} alignItems="center">
                  <Chip
                    icon={currentStatus.icon}
                    label={currentStatus.label}
                    size="small"
                    sx={{
                      bgcolor: currentStatus.bg,
                      color: currentStatus.color,
                      fontWeight: 600,
                      fontSize: "12px",
                      "& .MuiChip-icon": { color: currentStatus.color },
                    }}
                  />
                  <Stack alignItems="center">
                    <ReactStars
                      edit={false}
                      count={5}
                      value={get_user_by_id_data?.rating || 0}
                      size={18}
                      activeColor="#fbbf24"
                    />
                    <Typography sx={{ fontSize: "12px", color: "#888" }}>
                      {get_user_by_id_data?.rating || 0}/5
                    </Typography>
                  </Stack>
                </Stack>
              </Stack>
            </Stack>
          </Stack>

          <Grid container spacing={2.5}>
            {/* Left: Services + Stats */}
            <Grid item xs={12} md={8}>
              <Stack gap={2.5}>
                {/* Services */}
                <Stack
                  sx={{
                    bgcolor: "#fff",
                    borderRadius: "14px",
                    boxShadow: "0 1px 8px rgba(0,0,0,0.06)",
                    overflow: "hidden",
                  }}
                >
                  <Stack sx={{ px: 3, pt: 2.5, pb: 1.5 }}>
                    <Typography sx={{ fontSize: "16px", fontWeight: 700, color: themeBlue }}>
                      Services
                    </Typography>
                  </Stack>
                  <Box sx={{ overflow: "auto" }}>
                    <VendorServicesTable data={get_user_by_id_data?.services || []} />
                  </Box>
                </Stack>

                {/* Job Stats */}
                <Grid container spacing={2}>
                  {[
                    { label: "Completed", value: get_user_by_id_data?.completed_projects || 0, color: "#2E7D32", bg: "#E8F5E9" },
                    { label: "Accepted", value: get_user_by_id_data?.accepted_projects || 0, color: "#1565C0", bg: "#E3F2FD" },
                    { label: "Declined", value: get_user_by_id_data?.declined_projects || 0, color: "#C62828", bg: "#FFEBEE" },
                  ].map((stat, i) => (
                    <Grid item xs={4} key={i}>
                      <Stack
                        alignItems="center"
                        sx={{
                          bgcolor: "#fff",
                          borderRadius: "14px",
                          boxShadow: "0 1px 8px rgba(0,0,0,0.06)",
                          p: 2.5,
                        }}
                      >
                        <Box
                          sx={{
                            width: 42,
                            height: 42,
                            borderRadius: "10px",
                            bgcolor: stat.bg,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            mb: 1,
                          }}
                        >
                          <Typography sx={{ fontSize: "16px", fontWeight: 700, color: stat.color }}>
                            {String(stat.value).padStart(2, "0")}
                          </Typography>
                        </Box>
                        <Typography sx={{ fontSize: "12px", color: "#888", fontWeight: 500 }}>
                          {stat.label}
                        </Typography>
                      </Stack>
                    </Grid>
                  ))}
                </Grid>
              </Stack>
            </Grid>

            {/* Right: Status Update */}
            <Grid item xs={12} md={4}>
              <Stack
                sx={{
                  bgcolor: "#fff",
                  borderRadius: "14px",
                  boxShadow: "0 1px 8px rgba(0,0,0,0.06)",
                  p: 3,
                }}
                gap={2}
              >
                <Typography sx={{ fontSize: "16px", fontWeight: 700, color: themeBlue }}>
                  Update Status
                </Typography>
                <CustomSelectBox
                  options={statusArr}
                  isMulti={false}
                  value={status}
                  onChange={(e) => setStatus(e)}
                  placeholder="Select status"
                  customStyles={{
                    control: {
                      borderRadius: "10px",
                      height: "43px",
                      width: "100%",
                    },
                  }}
                />
                <ButtonComp
                  onClick={_handleStatusUpdate}
                  label={
                    isLoading ? (
                      <CircularProgress size={20} sx={{ color: "white" }} />
                    ) : (
                      "Save"
                    )
                  }
                  style={{
                    width: "100%",
                    borderRadius: "10px",
                    background: "linear-gradient(135deg, #F15A24 0%, #C53F10 100%)",
                  }}
                />
              </Stack>
            </Grid>
          </Grid>
        </Stack>
      </Container>
    </Stack>
  );
};

export default VendorProfileTab;
